import React, { useState } from "react";
import { BrowserRouter as Router, Link as RouterLink, Route, Routes, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import {
  Badge,
  Box,
  Button,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  ThemeProvider,
  Typography,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Sidebar from "./components/Sidebar";
import PlatformOverview from "./components/PlatformOverview";
import TextPhotoProcessor from "./components/TextPhotoProcessor";
import PerfumeDashboard from "./components/PerfumeDashboard";
import LabelingPage from "./components/LabelingPage";
import DocumentSubmission from "./components/DocumentSubmission";
import DocumentRepository from "./components/DocumentRepository";
import VendorManagement from "./components/VendorManagement";
import ProductComplianceTracker from "./components/ProductComplianceTracker";
import ComplianceAutomationHub from "./components/ComplianceAutomationHub";
import WorkflowAutomation from "./components/WorkflowAutomation";
import RegulatoryIntelligence from "./components/RegulatoryIntelligence";
import LandingPage from "./components/LandingPage";
import { findRouteMeta } from "./config/platformRoutes";
import { PlatformProvider, usePlatform } from "./platform/store";
import theme from "./theme";

const App = () => (
  <ThemeProvider theme={theme}>
    <CssBaseline />
    <PlatformProvider>
      <Router>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/app/*" element={<PlatformShell />} />
        </Routes>
      </Router>
    </PlatformProvider>
  </ThemeProvider>
);

const PlatformShell = () => {
  const location = useLocation();
  const routeMeta = findRouteMeta(location.pathname) || findRouteMeta("/app");
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("lg"));
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const { dashboard, issues, jobs, loading, error } = usePlatform();

  const blockingIssues = (issues || []).filter((i) => i.blocking);
  const runningJobs = (jobs || []).filter((j) => j.status === "Running" || j.status === "Queued");
  const isSyncing = runningJobs.length > 0;

  // Group blocking issues by product for a clean notification list.
  const issuesByProduct = blockingIssues.reduce((acc, issue) => {
    const key = issue.productId || "_";
    if (!acc.has(key)) {
      acc.set(key, {
        productId: issue.productId,
        productName: issue.productName || "Unknown product",
        count: 0,
        topCode: issue.code,
      });
    }
    acc.get(key).count += 1;
    return acc;
  }, new Map());
  const notificationGroups = [...issuesByProduct.values()].slice(0, 6);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      {/* Top bar — Liquid Glass CHROME tier */}
      <Box
        sx={{
          height: { xs: 52, md: 56 },
          px: { xs: 1.5, sm: 2, md: 3 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: muiTheme.glass.chrome.background,
          backdropFilter: muiTheme.glass.chrome.backdropFilter,
          WebkitBackdropFilter: muiTheme.glass.chrome.WebkitBackdropFilter,
          borderBottom: muiTheme.glass.chrome.border,
          boxShadow: muiTheme.glass.chrome.boxShadow,
          position: "sticky",
          top: 0,
          zIndex: 20,
          // Specular highlight along the top-left edge — the "wet rim" tell.
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background: muiTheme.glass.specular,
            opacity: 0.7,
            pointerEvents: "none",
            mixBlendMode: "soft-light",
            zIndex: 0,
          },
          "& > *": { position: "relative", zIndex: 1 },
          "@media print": {
            background: "#FFFFFF !important",
            backdropFilter: "none !important",
            WebkitBackdropFilter: "none !important",
            boxShadow: "none !important",
            "&::before": { display: "none" },
          },
        }}
      >
        {/* Left: menu + brand + location */}
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          {isMobile && (
            <IconButton size="small" aria-label="Open navigation" onClick={() => setMobileNavOpen(true)} sx={{ width: 32, height: 32 }}>
              <MenuIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <Box
            component={RouterLink}
            to="/app"
            aria-label="Go to Command Center"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              height: 32,
              textDecoration: "none",
            }}
          >
            {/* Brand wordmark — variant h4 is SF Pro Display 20px (Title 3) */}
            <Typography
              component="span"
              variant="h4"
              sx={{
                // Navy[700] preserved as the brand mark colour.
                color: "#1B2A4A",
                fontWeight: 700,
                lineHeight: "32px",
                display: "block",
                height: 32,
              }}
            >
              kodo
            </Typography>
          </Box>
          {!isMobile && routeMeta && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, height: 32 }}>
              <Typography
                component="span"
                variant="body2"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  userSelect: "none",
                  color: "rgba(0,0,0,0.25)",
                  lineHeight: "32px",
                  height: 32,
                }}
              >
                /
              </Typography>
              {/* Breadcrumb pill — INSET tier glass */}
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  height: 24,
                  px: 1.5,
                  borderRadius: 999, // pill
                  background: muiTheme.glass.inset.background,
                  backdropFilter: muiTheme.glass.inset.backdropFilter,
                  WebkitBackdropFilter: muiTheme.glass.inset.WebkitBackdropFilter,
                  border: muiTheme.glass.inset.border,
                  boxShadow: muiTheme.glass.inset.boxShadow,
                  transition: muiTheme.motion.tint,
                  minWidth: 0,
                }}
              >
                <Typography
                  component="span"
                  variant="subtitle2"
                  sx={{
                    color: "rgba(0,0,0,0.78)",
                    fontWeight: 500,
                    letterSpacing: "-0.005em",
                    minWidth: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    lineHeight: 1,
                  }}
                >
                  {routeMeta.title}
                </Typography>
              </Box>
            </Stack>
          )}
        </Stack>

        {/* Right: sync state + notifications */}
        <Stack direction="row" spacing={0.75} alignItems="center">
          {isSyncing && (
            <Chip
              icon={<CircularProgress size={10} sx={{ color: `${muiTheme.palette.primary.main} !important`, ml: "4px !important" }} />}
              label={runningJobs.length === 1 ? runningJobs[0].name : `${runningJobs.length} running`}
              size="small"
              sx={{
                // INSET tier glass with a navy tint behind it.
                background: `linear-gradient(rgba(27,42,74,0.10), rgba(27,42,74,0.10)), ${muiTheme.glass.inset.background}`,
                backdropFilter: muiTheme.glass.inset.backdropFilter,
                WebkitBackdropFilter: muiTheme.glass.inset.WebkitBackdropFilter,
                border: muiTheme.glass.inset.border,
                boxShadow: muiTheme.glass.inset.boxShadow,
                color: muiTheme.palette.primary.main,
                maxWidth: 200,
                "& .MuiChip-label": { fontWeight: 600 },
              }}
            />
          )}
          {!isSyncing && dashboard.blockingIssues === 0 && dashboard.products > 0 && (
            <Chip
              icon={<CheckCircleOutlineRoundedIcon sx={{ fontSize: "14px !important", color: "#2D6A4F !important" }} />}
              label="All clear"
              size="small"
              sx={{
                // INSET tier glass with a success tint behind it.
                background: `linear-gradient(rgba(45,106,79,0.14), rgba(45,106,79,0.14)), ${muiTheme.glass.inset.background}`,
                backdropFilter: muiTheme.glass.inset.backdropFilter,
                WebkitBackdropFilter: muiTheme.glass.inset.WebkitBackdropFilter,
                border: muiTheme.glass.inset.border,
                boxShadow: muiTheme.glass.inset.boxShadow,
                color: muiTheme.palette.success.main,
                display: { xs: "none", md: "inline-flex" },
              }}
            />
          )}
          <IconButton
            size="small"
            aria-label={`${blockingIssues.length} blocking notifications`}
            onClick={(e) => setNotifAnchor(e.currentTarget)}
            sx={{
              color: blockingIssues.length > 0 ? "error.main" : "text.secondary",
              width: 32,
              height: 32,
              borderRadius: "50%",
              // INSET tier — a soft glass disc so the icon button reads as chrome control.
              background: muiTheme.glass.inset.background,
              backdropFilter: muiTheme.glass.inset.backdropFilter,
              WebkitBackdropFilter: muiTheme.glass.inset.WebkitBackdropFilter,
              border: muiTheme.glass.inset.border,
              boxShadow: muiTheme.glass.inset.boxShadow,
              transition: `${muiTheme.motion.tint}, transform ${muiTheme.motion.press}`,
              "&:hover": { background: "rgba(255,255,255,0.32)" },
              "&:active": { transform: "scale(0.92)" },
            }}
          >
            <Badge
              badgeContent={blockingIssues.length}
              max={99}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              slotProps={{
                badge: {
                  sx: {
                    // Error severity hex — brand mark preserved.
                    bgcolor: muiTheme.palette.error.main,
                    color: muiTheme.palette.common.white,
                    // 11px = Caption 2 / overline scale, the smallest typography token.
                    fontSize: muiTheme.typography.overline.fontSize,
                    fontWeight: 600,
                    height: 16,
                    minWidth: 16,
                    padding: "0 4px",
                    border: "1px solid rgba(255,255,255,0.92)",
                    transform: "scale(1) translate(35%, -35%)",
                    transformOrigin: "100% 0%",
                  },
                },
              }}
            >
              <NotificationsNoneRoundedIcon sx={{ fontSize: 18 }} />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 280,
                  maxWidth: 360,
                  mt: 0.5,
                  // SURFACE tier glass — frosted popover.
                  background: muiTheme.glass.surface.background,
                  backdropFilter: muiTheme.glass.surface.backdropFilter,
                  WebkitBackdropFilter: muiTheme.glass.surface.WebkitBackdropFilter,
                  border: muiTheme.glass.surface.border,
                  boxShadow: muiTheme.glass.surface.boxShadow,
                  borderRadius: 2, // 16px — concentric to nav items inside
                  // Specular highlight on the popover surface.
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    background: muiTheme.glass.specular,
                    opacity: 0.7,
                    pointerEvents: "none",
                    mixBlendMode: "soft-light",
                  },
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="overline" sx={{ display: "block" }}>
                Notifications
              </Typography>
              <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500, mt: 0.5 }}>
                {blockingIssues.length > 0
                  ? `${blockingIssues.length} blocking issue${blockingIssues.length === 1 ? "" : "s"}`
                  : "No blocking issues"}
              </Typography>
            </Box>
            <Divider />
            {notificationGroups.length === 0 ? (
              <Box sx={{ px: 2, py: 2, textAlign: "center" }}>
                {/* Success-state mark — brand severity hex preserved. */}
                <CheckCircleOutlineRoundedIcon sx={{ fontSize: 24, color: "#2D6A4F", mb: 0.5 }} />
                <Typography variant="caption" sx={{ display: "block" }}>
                  Portfolio clear — nothing blocking release.
                </Typography>
              </Box>
            ) : (
              notificationGroups.map((group) => (
                <MenuItem
                  key={group.productId || group.productName}
                  component={RouterLink}
                  to={group.productId ? `/perfume-dashboard?product=${group.productId}` : "/compliance-tracker"}
                  onClick={() => setNotifAnchor(null)}
                  sx={{ py: 1 }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ width: "100%" }}>
                    {/* Error-state mark — brand severity hex preserved. */}
                    <ErrorOutlineRoundedIcon sx={{ fontSize: 16, color: "#991B1B", flexShrink: 0 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {group.productName}
                      </Typography>
                      <Typography variant="caption">
                        {group.count} blocking · {group.topCode}
                      </Typography>
                    </Box>
                  </Stack>
                </MenuItem>
              ))
            )}
            {blockingIssues.length > 0 && (
              <>
                <Divider />
                <MenuItem
                  component={RouterLink}
                  to="/compliance-tracker"
                  onClick={() => setNotifAnchor(null)}
                  sx={{ justifyContent: "center", color: "primary.main", fontWeight: 500, typography: "subtitle2" }}
                >
                  View all in Tracker →
                </MenuItem>
              </>
            )}
          </Menu>
        </Stack>
      </Box>

      <Box sx={{ flex: 1, display: "flex", minHeight: 0 }}>
        {!isMobile && <Sidebar />}
        {isMobile && (
          <Drawer
            open={mobileNavOpen}
            onClose={() => setMobileNavOpen(false)}
            // Liquid Glass spring curve — slightly longer ease-out on enter,
            // tighter snap on exit so dismiss feels responsive. Matches the
            // `theme.motion.morph` token used elsewhere.
            transitionDuration={{ enter: 320, exit: 240 }}
            slotProps={{
              paper: {
                sx: {
                  // Match the Sidebar's intrinsic width so the Drawer paper
                  // doesn't extend past it as an empty bordered strip.
                  width: 220,
                  borderRadius: 0,
                  display: "flex",
                  flexDirection: "column",
                  // Suppress the Drawer paper's redundant chrome — the
                  // Sidebar inside provides its own glass surface. Also kill
                  // the hairline border inherited from MuiPaper-root.
                  background: "transparent !important",
                  backdropFilter: "none !important",
                  WebkitBackdropFilter: "none !important",
                  boxShadow: "none !important",
                  border: "none !important",
                  "&::before": { display: "none" },
                  // Override the inner Slide transition timing — MUI's default
                  // Drawer slide is `easing.easeOut`; this swaps in the
                  // Liquid Glass morph cubic-bezier so the panel arrives with
                  // the same spring as the rest of the chrome.
                  "&.MuiDrawer-paper": {
                    transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1) !important",
                  },
                  // Subtle content fade-in on open — the nav items appear
                  // a beat after the panel arrives, so the sidebar feels
                  // like it's resolving into place rather than snapping.
                  "& > nav": {
                    animation: "drawerContentIn 360ms cubic-bezier(0.32, 0.72, 0, 1) both",
                  },
                  "@keyframes drawerContentIn": {
                    "0%": { opacity: 0, transform: "translateX(-6px)" },
                    "100%": { opacity: 1, transform: "translateX(0)" },
                  },
                },
              },
              // Hide the Modal backdrop — the large dim rectangle that
              // covers the rest of the screen when the Drawer is open.
              // The user can still tap outside the sidebar to close.
              backdrop: {
                sx: { background: "transparent" },
              },
            }}
          >
            <Sidebar onNavigate={() => setMobileNavOpen(false)} />
          </Drawer>
        )}

        {/* Main area — kept transparent so the page gradient shows through the chrome glass. */}
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            overflowY: "auto",
            overflowX: "hidden",
            px: { xs: 2, sm: 3, md: 4, lg: 5 },
            py: { xs: 2.5, sm: 3, md: 4 },
            background: "transparent",
          }}
        >
          <Box sx={{ mb: { xs: 2.5, sm: 3, md: 4 }, animation: "fadeIn 300ms ease" }}>
            <Typography variant="h2">{routeMeta.title}</Typography>
            <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "text.secondary" }}>
              {routeMeta.description}
            </Typography>
          </Box>

          {loading ? (
            <Box sx={{ py: 10, display: "grid", placeItems: "center", gap: 1.5 }}>
              <CircularProgress size={20} sx={{ color: "text.secondary" }} />
            </Box>
          ) : error ? (
            <Typography color="error" variant="body2">{error}</Typography>
          ) : routeMeta?.enabled === false ? (
            <DisabledModule routeMeta={routeMeta} />
          ) : (
            <Box sx={{ animation: "fadeInUp 400ms ease" }}>
              <Routes>
                <Route index element={<PlatformOverview />} />
                <Route path="compliance-checker" element={<TextPhotoProcessor />} />
                <Route path="perfume-dashboard" element={<PerfumeDashboard />} />
                <Route path="labeling" element={<LabelingPage />} />
                <Route path="document-submission" element={<DocumentSubmission />} />
                <Route path="document-repository" element={<DocumentRepository />} />
                <Route path="vendor-management" element={<VendorManagement />} />
                <Route path="compliance-tracker" element={<ProductComplianceTracker />} />
                <Route path="compliance-automation" element={<ComplianceAutomationHub />} />
                <Route path="workflow-automation" element={<WorkflowAutomation />} />
                <Route path="regulatory-intelligence" element={<RegulatoryIntelligence />} />
              </Routes>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

const DisabledModule = ({ routeMeta }) => (
  <Box sx={{ maxWidth: 420, py: 4 }}>
    <Chip label="Coming soon" size="small" sx={{ mb: 1.5 }} />
    <Typography variant="h2" sx={{ mb: 1 }}>{routeMeta.title}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
      {routeMeta.disabledReason || "This module is not yet available."}
    </Typography>
    <Button href="/app" variant="outlined" size="small">Back to overview</Button>
  </Box>
);

export default App;
